import { useCallback, useState } from 'react';
import TransformationIndicator from './TransformationIndicator';

interface PDFUploadProps {
  onFileSelect: (file: File) => void;
  onError?: (message: string) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function PDFUpload({ onFileSelect, onError }: PDFUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Please upload a PDF file. Other file types are not supported.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds the maximum limit of ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB.`;
    }
    if (file.size === 0) {
      return 'The selected file is empty. Please choose a valid PDF file.';
    }
    return null;
  }, []);

  const handleFile = useCallback((file: File) => {
    setValidationError(null);
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      if (onError) {
        onError(error);
      }
      return;
    }
    onFileSelect(file);
  }, [onFileSelect, onError, validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleButtonClick = useCallback(() => {
    document.getElementById('pdf-upload-input')?.click();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center w-full">
      {/* Visually hidden H1 for SEO and accessibility */}
      <h1 className="sr-only">PDF to Sheets Widget</h1>

      <div
        className={`
          w-full max-w-4xl rounded-2xl md:border-4 border-2 border-dashed transition-all duration-200 cursor-pointer
          ${isDragging
            ? 'border-emerald-500 bg-primary-50 scale-[1.02] shadow-lg'
            : 'border-gray-300 bg-white hover:border-emerald-400 hover:bg-gray-50'
          }
          ${validationError ? 'border-red-300 bg-red-50' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
        role="button"
        tabIndex={0}
        aria-label="PDF upload area"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleButtonClick();
          }
        }}
      >
        <div className="flex flex-col items-center justify-center lg:p-16 p-4 text-center">
          {/* Transformation Indicator */}
          <TransformationIndicator state="idle" />

          <p className="md:text-4xl text-2xl font-bold text-gray-900 md:mb-3 mb-2 md:mt-4 mt-0">
            {isDragging ? 'Drop your PDF here' : 'Drop your PDF to get a Google Sheet'}
          </p>
          <p className="md:text-lg text-base text-gray-600 md:mb-3 mb-2">
            Tables and data extracted automatically. No signup.
          </p>
          <p className="md:text-base text-sm text-gray-500 md:mb-8 mb-4">
            Maximum file size: {(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)}MB
          </p>

          {validationError && (
            <div 
              className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm max-w-md"
              role="alert"
            >
              {validationError}
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick();
            }}
            className="px-10 py-4 text-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold rounded-lg shadow-md hover:from-emerald-600 hover:to-green-600 active:from-emerald-700 active:to-green-700 transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Browse files to upload PDF"
          >
            Browse Files
          </button>

          <input
            id="pdf-upload-input"
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileInput}
            className="hidden"
            aria-label="PDF file input"
          />
        </div>
      </div>
    </div>
  );
}

